/**
 * caseStageLabels — PLACEHOLDER human names for the FR-10 case-spine stages.
 *
 * The BE emits machine stage `key`s and leaves `stage.label` NULL on purpose:
 * the human stage NAME is counsel-gated (§10 — `case_stage_label` returns None
 * until counsel approves the exact wording, the PRIOR_OWNER_VERIFY_COPY
 * pattern). "Decision" vs "Certification" is legally load-bearing — CO-S2/CO-S4:
 * the reopen clock and the cure clock are DIFFERENT clocks, and a wrong name
 * conflates them.
 *
 * So the FE renders a PLACEHOLDER label from this map, mirroring the BE
 * `STAGE_KEYS` variant→key structure verbatim, and MARKS it as a working title
 * (`CASE_STAGE_LABELS_COUNSEL_PENDING`) so the surface never presents these as
 * settled legal language. The moment the BE ships a non-null `stage.label`,
 * `resolveStageLabel` prefers it and the placeholder falls away with zero
 * component churn.
 *
 * These placeholders are intentionally NEUTRAL and NON-CONFLATING (they keep the
 * machine key's own distinction — "Reopen decision" ≠ "Certification") so even
 * the working title cannot merge two clocks.
 */
import type { CaseVariant, CaseStage } from "@/queries/violationCasesQueries";

/** Working titles — NOT counsel-approved. See `CASE_STAGE_LABELS_COUNSEL_PENDING`. */
export const CASE_STAGE_LABELS_COUNSEL_PENDING = true as const;

/**
 * variant → (machine stage key → placeholder label). Mirrors the BE
 * `STAGE_KEYS` in `services_violation_engagement.py` exactly (order + keys).
 */
export const CASE_STAGE_LABELS: Record<CaseVariant, Record<string, string>> = {
  contest: {
    issued: "Ticket issued",
    hearing: "Hearing",
    decision: "Decision",
    resolved: "Resolved",
  },
  reopen: {
    defaulted: "Defaulted",
    reopen_filing: "Reopen filing",
    reopen_decision: "Reopen decision",
    resolved: "Resolved",
  },
  cure_certify: {
    issued: "Ticket issued",
    correction: "Correction",
    certification: "Certification",
    accepted: "Accepted",
  },
  appeal: {
    adjudicated: "Adjudicated",
    appeal_filing: "Appeal filing",
    appeal_decision: "Appeal decision",
    resolved: "Resolved",
  },
  na_terminal: {
    not_applicable: "Not applicable",
  },
};

/** Humanize an unknown machine key ("reopen_decision" → "Reopen decision") —
 *  the last-resort fallback so a BE stage key we didn't map still reads. */
export const humanizeStageKey = (key: string): string => {
  const spaced = key.replace(/_/g, " ").trim();
  if (!spaced) return key;
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

/**
 * The placeholder label for a (variant, key) — falls back to a humanized key
 * for any stage the map doesn't cover (defensive; the map mirrors the BE).
 */
export const caseStageLabel = (
  variant: CaseVariant | null,
  key: string,
): string => {
  const forVariant = variant ? CASE_STAGE_LABELS[variant] : undefined;
  return forVariant?.[key] ?? humanizeStageKey(key);
};

/**
 * Resolve the label a component should render for a spine stage: prefer the
 * BE's counsel-approved `stage.label` the instant it ships; otherwise the FE
 * placeholder. Surfaces that show placeholders MUST also render the
 * `CASE_STAGE_LABELS_COUNSEL_PENDING` marker.
 */
export const resolveStageLabel = (
  variant: CaseVariant | null,
  stage: Pick<CaseStage, "key" | "label">,
): string => stage.label ?? caseStageLabel(variant, stage.key);

/** True when the resolved label is a FE placeholder (BE label still null). */
export const isPlaceholderStageLabel = (
  stage: Pick<CaseStage, "label">,
): boolean => stage.label == null;
